import { logError } from '@ptek/client-logger';
import { apiRouter } from '../config/api';
import { WHISPER_CONFIG } from '../config/whisper';
import type { FeatureFlags } from '../types/featureFlags';
import { SessionPatient } from '../types/session';
import type {
  ContextItemCount,
  StartSetup,
  WidgetConfigFlags,
} from '../utils/buildStartSetup';
import { authManager } from './whisperClient';

export type { SessionPatient };

export interface TranscriptSegment {
  id: string;
  speaker: string;
  timestamp: string;
  text: string;
  confidence: number;
}

export interface SessionDocument {
  id: string;
  type:
    | 'chart_note'
    | 'exercise_plan'
    | 'referral_letter'
    | 'transcript'
    | 'SOAP_NOTE'
    | string;
  title: string;
  content: string;
  status: 'pending' | 'approved' | 'needs_revision';
  generatedAt: string;
  templateId?: string;
  contentSource?: string;
  storageLocation?: string;
  documentType?: string;
  segmentCount?: number;
  wordCount?: number;
  speakers?: string[];
  speakerSegmentCounts?: Record<string, number>;
  whisperSessionId?: string;
  contentSize?: number;
  processedBy?: string;
  processedAt?: string;
  modelId?: string;
  processingTime?: number;
  stats?: {
    wordCount: number;
    segmentCount: number;
  };
  isEdited?: boolean;
  updatedAt?: string;
  updatedBy?: string;
}

export interface LastTake {
  userId: string;
  deviceId: string | null;
  pausedAt: string | null;
}

export interface ActiveRecorder {
  deviceId: string | null;
  leaseExpiresAt: number;
}

export interface Session {
  id: string;
  sessionId?: string;
  patientName: string;
  encounterId?: string;
  visitDate: string;
  visitType: string;
  duration: number;
  provider: string;
  transcript: TranscriptSegment[];
  documents: SessionDocument[];
  status?: string;
  createdAt?: number;
  updatedAt?: number;
  title?: string;
  customerId?: string;
  userId?: string;
  lastTake?: LastTake | null;
  activeRecorder?: ActiveRecorder;
  recordingPaused?: boolean;
}

export interface CreateSessionRequest {
  patientName: string;
  encounterId?: string;
  visitDate: string;
  visitType: string;
  duration: number;
  provider: string;
  transcript: TranscriptSegment[];
  /** Names and metadata of what the session started with (no values). */
  startSetup?: StartSetup;
}

export interface UpdateSessionRequest {
  visitDate?: string;
  visitType?: string;
  duration?: number;
  provider?: string;
  transcript?: TranscriptSegment[];
  status?: 'pending_review' | 'approved' | 'needs_revision';
  encounterId?: string;
  patientName?: string;
}

export interface SessionFilters {
  status?: 'pending_review' | 'approved' | 'needs_revision';
  dateFrom?: string;
  dateTo?: string;
  provider?: string;
  encounterId?: string;
}

export interface UploadTranscriptRequest {
  segments: Array<{
    id: string;
    speaker: string;
    timestamp: string;
    text: string;
    confidence: number;
  }>;
  whisperSessionId: string;
  processingTimeMs?: number;
  totalDurationSeconds?: number;
  recordingDurationMs?: number;
}

export interface UploadTranscriptResponse {
  success: boolean;
  sessionId: string;
  transcriptDocumentId: string;
  sessionStatus: string;
  stats: {
    segmentCount: number;
    wordCount: number;
  };
  metadata: {
    processedBy: string;
    processedAt: string;
  };
}

export interface BeginTakeResponse {
  success: boolean;
  sessionId: string;
  takeGeneration: number;
  /** Server-authoritative floor for the next chunk number in this session. */
  nextChunkNumber?: number;
  leaseId?: string;
  leaseExpiresAt?: number;
}

export interface TemplateSnapshotRequest {
  template: Record<string, unknown>;
  takeGeneration: number;
  snapshotVersion: number;
  context?: {
    structured?: Record<string, unknown>;
    unstructured?: string;
  };
  /** In the unified flow the snapshot is what the server's generation records these from. */
  features?: FeatureFlags;
  widgetConfig?: WidgetConfigFlags;
  contextItems?: ContextItemCount[];
}

export interface TemplateSnapshotResponse {
  success: boolean;
  sessionId: string;
}

export interface StitchDocGenRequestResponse {
  sessionId: string;
  takeGeneration: number;
  status: 'PENDING' | 'ENQUEUING' | 'ENQUEUED' | 'FAILED' | null;
  jobId: string | null;
  errorCode?: 'MISSING_TEMPLATE_SNAPSHOT' | null;
}

export interface RecordingCompleteRequest {
  finalChunkNumber?: number;
  takeStart?: number;
  takeGeneration?: number;
  leaseId?: string;
  leaseTakeGeneration?: number;
  action?: 'pause' | 'stop';
}

export interface RecordingCompleteResponse {
  success: boolean;
  sessionId: string;
  /** Whether the completeness/stitch finalize actually ran for this signal. */
  finalized?: boolean;
  /**
   * Pause path only: whether the server durably checkpointed the take. False on
   * idempotent off-take no-ops even though the call returns 200.
   */
  checkpointed?: boolean;
  action?: 'pause' | 'stop';
  /** Pause checkpoint document id, when a checkpoint was written. */
  documentId?: string;
}

export type FeedbackRating = 'up' | 'down';

/** The API accepts the first four only with 'up' and the last four only with 'down'. */
export type FeedbackReasonCode =
  | 'accurate'
  | 'complete'
  | 'well_organized'
  | 'saved_me_time'
  | 'inaccurate'
  | 'missed_details'
  | 'formatting'
  | 'required_edits';

/** Mirrors MAX_FEEDBACK_COMMENT_LENGTH in the API events route. */
export const MAX_FEEDBACK_COMMENT_LENGTH = 1000;

/** A feedback body for POST /v1/sessions/{id}/events; any other key is a 400. */
export interface FeedbackEventRequest {
  type: 'feedback';
  /** 'rating' for a thumb click, 'enrichment' for a Submit of reasons/comment. */
  kind: 'rating' | 'enrichment';
  feedbackId: string;
  documentId: string;
  rating: FeedbackRating;
  reasonCodes?: FeedbackReasonCode[];
  comment?: string;
}

/**
 * `status` is null when no HTTP response arrived; `code` is the error body's
 * `code` (e.g. 'feedback_limit_reached'), null when there is none.
 */
export type SessionEventResult =
  | { ok: true }
  | { ok: false; status: number | null; code: string | null };

const readErrorCode = (body: unknown): string | null => {
  if (typeof body !== 'string') return null;
  try {
    const code = (JSON.parse(body) as { code?: unknown } | null)?.code;
    return typeof code === 'string' ? code : null;
  } catch {
    return null;
  }
};

export class SessionRecordingConflictError extends Error {
  readonly status = 409;
  readonly code = 'ACTIVE_RECORDER';

  constructor(
    message = 'Scribe is already recording\nOnly one Scribe session can be active at a time. This encounter is currently recording on a different computer. Pause or stop that recording before resuming here.'
  ) {
    super(message);
    this.name = 'SessionRecordingConflictError';
  }
}

export interface SessionWithDocuments {
  session: Session;
  documents: SessionDocument[];
  totalDocuments: number;
  lastTake?: LastTake | null;
  activeRecorder?: ActiveRecorder;
  recordingPaused?: boolean;
  metadata?: {
    accessLevel: string;
    storageLocation: string;
    userContext: {
      market: string;
      brand: string;
      customerId: string;
    };
  };
}

/**
 * Unified Sessions API client for managing recording sessions
 * Supports both Whisper backend and main API Gateway endpoints
 */
export class SessionsClient {
  private whisperConfig = WHISPER_CONFIG;

  /**
   * Get authentication headers for Whisper API requests
   */
  private async getWhisperAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Client-Id': 'ambient-scribe-widget',
    };

    const token = await authManager.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Make API call using the router that automatically directs to legacy or new service
   */
  private async makeApiCall(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<unknown> {
    return apiRouter.makeApiCall(endpoint, options);
  }

  /**
   * Make API call to Whisper backend
   */
  private async makeWhisperApiCall(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<unknown> {
    const url = `${this.whisperConfig.restApiUrl}${endpoint}`;
    const headers = await this.getWhisperAuthHeaders();

    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...(options.headers as Record<string, string>),
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required');
      } else if (response.status === 403) {
        throw new Error('Access denied');
      } else if (response.status === 404) {
        throw new Error('Resource not found');
      } else {
        throw new Error(
          `API call failed: ${response.status} ${response.statusText}`
        );
      }
    }

    return response.json();
  }

  /**
   * Get specific session by ID
   */
  public async getSession(
    sessionId: string,
    options: { includeLastTake?: boolean } = {}
  ): Promise<Session | null> {
    try {
      const endpoint = options.includeLastTake
        ? `/api/v1/sessions/${sessionId}?includeLastTake=true`
        : `/api/v1/sessions/${sessionId}`;
      const data = (await this.makeApiCall(endpoint)) as { session: Session };
      return data.session || null;
    } catch (error) {
      logError(
        'SessionsClient',
        `Error getting session ${sessionId}:`,
        error instanceof Error ? error.message : 'Unknown error'
      );
      return null;
    }
  }

  /**
   * Get session with associated documents by session ID
   */
  public async getSessionWithDocuments(
    sessionId: string,
    options: { includeLastTake?: boolean } = {}
  ): Promise<SessionWithDocuments | null> {
    try {
      const endpoint = options.includeLastTake
        ? `/api/v1/sessions/${sessionId}/documents?includeLastTake=true`
        : `/api/v1/sessions/${sessionId}/documents`;

      try {
        const data = (await this.makeApiCall(endpoint)) as {
          session?: Session;
          sessionId?: string;
          documents?: SessionDocument[];
          totalDocuments?: number;
          duration?: number;
          lastTake?: LastTake | null;
          activeRecorder?: ActiveRecorder;
          recordingPaused?: boolean;
          metadata?: {
            accessLevel: string;
            storageLocation: string;
            userContext: {
              market: string;
              brand: string;
              customerId: string;
            };
          };
        };

        // Parse session data - handle both nested and flattened formats
        let session: Session;
        if (data.session) {
          session = data.session;
        } else if (data.sessionId) {
          session = {
            id: data.sessionId,
            sessionId: data.sessionId,
            patientName: '',
            visitDate: '',
            visitType: '',
            // Hydrate the recorded length from the persisted session duration the
            // /documents endpoint now returns; fall back to 0 only when the API
            // omits it (e.g. not yet deployed). (NEB-93248)
            duration: data.duration ?? 0,
            provider: '',
            transcript: [],
            documents: data.documents || [],
            ...(data.lastTake !== undefined && {
              lastTake: data.lastTake,
            }),
            ...(data.activeRecorder !== undefined && {
              activeRecorder: data.activeRecorder,
            }),
            ...(data.recordingPaused !== undefined && {
              recordingPaused: data.recordingPaused,
            }),
          } as Session;
        } else {
          return null;
        }

        return {
          session,
          documents: data.documents || [],
          totalDocuments: data.totalDocuments || data.documents?.length || 0,
          ...(data.lastTake !== undefined && { lastTake: data.lastTake }),
          ...(data.activeRecorder !== undefined && {
            activeRecorder: data.activeRecorder,
          }),
          ...(data.recordingPaused !== undefined && {
            recordingPaused: data.recordingPaused,
          }),
          metadata: data.metadata,
        };
      } catch (endpointError) {
        // If specific endpoint doesn't exist, try getting session separately
        if (
          endpointError instanceof Error &&
          (endpointError.message.includes('404') ||
            endpointError.message.includes('405'))
        ) {
          const session = await this.getSession(sessionId, options);
          if (!session) {
            return null;
          }

          // Return session with empty documents for now
          // TODO: Implement proper document fetching when backend supports it
          return {
            session,
            documents: session.documents || [],
            totalDocuments: session.documents?.length || 0,
            ...(session.lastTake !== undefined && {
              lastTake: session.lastTake,
            }),
            ...(session.activeRecorder !== undefined && {
              activeRecorder: session.activeRecorder,
            }),
            ...(session.recordingPaused !== undefined && {
              recordingPaused: session.recordingPaused,
            }),
            metadata: undefined,
          };
        }

        throw endpointError;
      }
    } catch (error) {
      logError(
        'SessionsClient',
        `Error getting session with documents (${sessionId}):`,
        error instanceof Error ? error.message : 'Unknown error'
      );
      return null;
    }
  }

  /**
   * Create a new recording session
   */
  public async createSession(
    sessionData: CreateSessionRequest
  ): Promise<Session> {
    try {
      // Try the main API Gateway, then fall back to Whisper.
      const data = (await this.makeApiCall('/api/v1/sessions', {
        method: 'POST',
        body: JSON.stringify(sessionData),
      })) as { data: Session };

      return data.data;
    } catch (error) {
      try {
        const data = (await this.makeWhisperApiCall('/sessions', {
          method: 'POST',
          body: JSON.stringify(sessionData),
        })) as { session: Session };

        return data.session;
      } catch (whisperError) {
        logError('SessionsClient', 'Failed to create session with both APIs:', {
          mainApiError: error,
          whisperApiError: whisperError,
        });
        throw whisperError;
      }
    }
  }

  /**
   * Update an existing recording session
   */
  public async updateSession(
    sessionId: string,
    updates: UpdateSessionRequest
  ): Promise<Session> {
    try {
      // Try the main API Gateway, then fall back to Whisper.
      const data = (await this.makeApiCall(`/api/v1/sessions/${sessionId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      })) as { data: Session };

      return data.data;
    } catch (error) {
      try {
        const data = (await this.makeWhisperApiCall(`/sessions/${sessionId}`, {
          method: 'PUT',
          body: JSON.stringify(updates),
        })) as { session: Session };

        return data.session;
      } catch (whisperError) {
        logError(
          'SessionsClient',
          `Failed to update session ${sessionId} with both APIs:`,
          { mainApiError: error, whisperApiError: whisperError }
        );
        throw whisperError;
      }
    }
  }

  /**
   * Delete all documents for a session (S3 + DynamoDB)
   */
  public async deleteSessionDocuments(
    sessionId: string
  ): Promise<{ sessionId: string; message: string }> {
    const data = (await this.makeApiCall(
      `/api/v1/sessions/${sessionId}/documents`,
      { method: 'DELETE' }
    )) as { sessionId: string; message: string };

    return data;
  }

  /**
   * Upload transcript to session using dedicated transcript endpoint
   */
  public async uploadTranscript(
    sessionId: string,
    transcriptData: UploadTranscriptRequest
  ): Promise<UploadTranscriptResponse> {
    try {
      const data = (await this.makeApiCall(
        `/api/v1/sessions/${sessionId}/transcript`,
        {
          method: 'POST',
          body: JSON.stringify(transcriptData),
        }
      )) as UploadTranscriptResponse;

      return data;
    } catch (error) {
      logError(
        'SessionsClient',
        `Error uploading transcript for session ${sessionId}:`,
        error instanceof Error ? error.message : 'Unknown error'
      );
      throw error;
    }
  }

  public async putTemplateSnapshot(
    sessionId: string,
    request: TemplateSnapshotRequest
  ): Promise<TemplateSnapshotResponse> {
    return (await this.makeApiCall(
      `/api/v1/sessions/${sessionId}/template-snapshot`,
      {
        method: 'PUT',
        body: JSON.stringify(request),
      }
    )) as TemplateSnapshotResponse;
  }

  public async getStitchDocGenRequest(
    sessionId: string,
    takeGeneration: number
  ): Promise<StitchDocGenRequestResponse> {
    return (await this.makeApiCall(
      `/api/v1/sessions/${sessionId}/docgen-request?takeGeneration=${takeGeneration}`,
      { method: 'GET' }
    )) as StitchDocGenRequestResponse;
  }

  /**
   * `continuous` declares a continuous-capture run so the server persists that
   * on the session; the recorder lease is issued off that durable marker, so
   * omitting the flag on a session that already declared it changes nothing.
   */
  public async postBeginTake(
    sessionId: string,
    takeStartChunk: number,
    deviceIdOrContinuous?: string | boolean,
    leaseId?: string,
    continuous?: boolean
  ): Promise<BeginTakeResponse> {
    const deviceId =
      typeof deviceIdOrContinuous === 'string'
        ? deviceIdOrContinuous
        : undefined;
    const isContinuous =
      typeof deviceIdOrContinuous === 'boolean'
        ? deviceIdOrContinuous
        : continuous;
    try {
      return (await this.makeApiCall(
        `/api/v1/sessions/${sessionId}/begin-take`,
        {
          method: 'POST',
          body: JSON.stringify({
            takeStartChunk,
            ...(deviceId && { deviceId }),
            ...(leaseId && { leaseId }),
            ...(isContinuous && { continuous: true }),
          }),
        }
      )) as BeginTakeResponse;
    } catch (error) {
      if ((error as { status?: unknown } | null)?.status === 409) {
        throw new SessionRecordingConflictError();
      }
      throw error;
    }
  }

  public async postRecordingComplete(
    sessionId: string,
    body: RecordingCompleteRequest
  ): Promise<RecordingCompleteResponse> {
    try {
      return (await this.makeApiCall(
        `/api/v1/sessions/${sessionId}/recording-complete`,
        { method: 'POST', body: JSON.stringify(body) }
      )) as RecordingCompleteResponse;
    } catch (error) {
      // A 409 on the pause path is the recorder-lease fence (a stale/foreign
      // lease); surface it as the same typed conflict beginTake maps so callers
      // can distinguish it from a transient failure.
      if ((error as { status?: unknown } | null)?.status === 409) {
        throw new SessionRecordingConflictError();
      }
      throw error;
    }
  }

  /**
   * Records one event on a note. Resolves instead of throwing so the caller
   * can tell an accepted event from a 4xx/5xx.
   */
  public async postSessionEvent(
    sessionId: string,
    event: FeedbackEventRequest
  ): Promise<SessionEventResult> {
    try {
      await this.makeApiCall(`/api/v1/sessions/${sessionId}/events`, {
        method: 'POST',
        body: JSON.stringify(event),
      });
      return { ok: true };
    } catch (error) {
      const { status, responseBody } = (error ?? {}) as {
        status?: unknown;
        responseBody?: unknown;
      };
      return {
        ok: false,
        status: typeof status === 'number' ? status : null,
        code: readErrorCode(responseBody),
      };
    }
  }
}

let sessionsClientInstance: SessionsClient | null = null;

/**
 * Get the singleton SessionsClient instance
 */
function getSessionsClient(): SessionsClient {
  if (!sessionsClientInstance) {
    sessionsClientInstance = new SessionsClient();
  }
  return sessionsClientInstance;
}

export const sessionsClient = getSessionsClient();
